import { useAnimate, useInView } from "framer-motion";
import * as Sentry from "@sentry/nextjs";
import { AspectRatio, Flex } from "@chakra-ui/react";
import Image from "next/image";
import React, { useEffect, useState } from "react";
import { ChakraLine, ChakraSvg } from "ui/chakra-framer";
import firstColumnImage from "../../../../../../public/assets/solutions/advertising/mb-column-1.png";
import secondColumnImage from "../../../../../../public/assets/solutions/advertising/mb-column-2.png";
import thirdColumnImage from "../../../../../../public/assets/solutions/advertising/mb-column-3.png";

function useMatchBoosterAnimation(isActive: boolean) {
  const [scope, animate] = useAnimate();

  useEffect(() => {
    const triggerAnimation = async () => {
      try {
        await animate(
          isActive
            ? [
                // Animation sequence
                ["#warehouse-to-ht-line", { pathLength: 1 }, { delay: 0.2, duration: 0.4 }],
                ["#ht-match-booster", { opacity: 1 }, { at: "-0.2", duration: 0.2 }],
                ["#ht-to-ads-line", { pathLength: 1 }, { delay: 0.1, duration: 0.4 }],
                ["#your-ad-platforms", { opacity: 1 }, { at: "-0.2", duration: 0.2 }],
              ]
            : [
                // Reset
                ["#ht-match-booster", { opacity: 0 }, { duration: 0.01 }],
                ["#your-ad-platforms", { opacity: 0 }, { duration: 0.01 }],
                ["#warehouse-to-ht-line", { pathLength: 0 }, { duration: 0.01 }],
                ["#ht-to-ads-line", { pathLength: 0 }, { duration: 0.01 }],
              ],
        );
      } catch (error) {
        Sentry.captureException(error);
      }
    };

    // Do not need `await` because there is no action after the promise
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    triggerAnimation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  return scope;
}

export function MatchBoosterOverviewAnimation() {
  const [animationActive, setAnimationActive] = useState(false);

  const scope = useMatchBoosterAnimation(animationActive);

  const isInView = useInView(scope, { once: true, amount: 0.9 });

  useEffect(() => {
    setAnimationActive(isInView);
  }, [isInView]);

  return (
    <Flex ref={scope} width="100%" maxW="1077px" height="auto" gap={{ base: "big", lg: "biggest" }} pos="relative">
      <ChakraSvg pos="absolute" inset={0} w="100%" height="auto" viewBox="0 0 1077 363">
        <ChakraLine
          id="warehouse-to-ht-line"
          x1="232"
          y1="196.207"
          x2="324"
          y2="196.207"
          stroke="#00A341"
          strokeWidth="1.2"
          initial={{
            pathLength: 0,
          }}
        />
        <ChakraLine
          id="ht-to-ads-line"
          x1="740"
          y1="196.207"
          x2="832"
          y2="196.207"
          stroke="#00A341"
          strokeWidth="1.2"
          initial={{
            pathLength: 0,
          }}
        />
      </ChakraSvg>

      <AspectRatio id="your-data-warehouse" ratio={506 / 717} pos="relative" w="253px">
        <Image fill src={firstColumnImage} alt="Your data warehouse." sizes="100%" style={{ objectFit: "contain" }} />
      </AspectRatio>

      <AspectRatio id="ht-match-booster" ratio={912 / 717} pos="relative" w="456px">
        <Image
          fill
          src={secondColumnImage}
          alt="Hightouch match booster."
          sizes="100%"
          style={{ objectFit: "contain" }}
        />
      </AspectRatio>

      <AspectRatio id="your-ad-platforms" ratio={528 / 717} pos="relative" w="264px">
        <Image fill src={thirdColumnImage} alt="Your ad platforms." sizes="100%" style={{ objectFit: "contain" }} />
      </AspectRatio>
    </Flex>
  );
}
